import { makeid } from 'utils/helpers/makeid';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createInternalNurseEscalationTemplate = (data: ITemplateProps) => {
  const { userFullname, clinicName } = data;

  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Thank you for calling Tellihealth. My name is {userFullname}, I'm a
          medical assistant calling on behalf of {clinicName}."
          <br />
          "For quality and compliance purposes, this call may be recorded."
          <br />
          "To protect your privacy, can you please confirm your full name and
          date of birth?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'patientConcernIdentified',
      type: 'checkbox',
      title: <p>Patient Concern Identified</p>,
      description: (
        <ScriptWrapper>
          "Thank you for explaining what's going on."
          <br />
          "I want to make sure you get the right support for this."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Medical advice related',
          value: 'medicalAdviceRelated',
        },
        {
          key: makeid(10),
          label: 'Symptom-related',
          value: 'symptomRelated',
        },
        {
          key: makeid(10),
          label: 'Medication-related',
          value: 'medicationRelated',
        },
        {
          key: makeid(10),
          label: 'Other out-of-scope concern',
          value: 'otherOutOfScopeConcern',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'escalationDetermination',
      type: 'checkbox',
      title: <p>Escalation Determination</p>,
      description: (
        <ScriptWrapper>
          "Based on what you're describing, this is something that needs to be
          reviewed by a nurse."
          <br />
          "My role is to help with program support and basic questions, but
          anything related to medical advice or symptoms is handled by our
          nursing team."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Escalation to nurse required',
          value: 'escalationToNurseRequired',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'patientNotifiedOfEscalation',
      type: 'checkbox',
      title: <p>Patient Notified of Escalation</p>,
      description: (
        <ScriptWrapper>
          "Connecting you with a nurse helps make sure your concern is reviewed
          appropriately and safely."
          <br />
          "They're the right person to guide next steps or decide if any
          follow-up is needed."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Yes',
          value: 'yes',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'resistanceEncountered',
      type: 'radio',
      title: <p>Resistance Encountered</p>,
      description: (
        <ScriptWrapper>
          If patient says "I don't need a nurse": "I understand, and I want to
          respect your time. My responsibility is to escalate concerns like this
          so a nurse can review them and decide the best next step."
          <br />
          If patient says "Can't you just answer": "I wish I could help more
          directly, but this is outside what I'm allowed to advise on. A nurse
          needs to review this to keep you safe."
          <br />
          If patient is frustrated: "I hear your concern, and I don't want you
          to feel bounced around. The nurse will review exactly what you've
          shared so you won't need to repeat everything."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'No resistance',
          value: 'noResistance',
        },
        {
          key: makeid(10),
          label: 'Mild resistance',
          value: 'mildResistance',
        },
        {
          key: makeid(10),
          label: 'Significant resistance',
          value: 'significantResistance',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'warmHandoffAttempted',
      type: 'radio',
      title: <p>Warm Handoff Attempted</p>,
      description: (
        <ScriptWrapper>
          If nurse is available: "I'm going to place you on a brief hold while I
          connect you directly with a nurse."
          <br />
          If nurse is not available: "A nurse will call you back as soon as
          possible."
          <br />
          "If anything worsens while you're waiting, please contact your clinic
          directly or seek urgent care."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Completed',
          value: 'completed',
        },
        {
          key: makeid(10),
          label: 'Attempted but nurse unavailable',
          value: 'attemptedButNurseUnavailable',
        },
        {
          key: makeid(10),
          label: 'Not attempted — nurse unavailable',
          value: 'notAttemptedNurseUnavailable',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'nurseCallbackRequired',
      type: 'radio',
      title: <p>Nurse Callback Required</p>,
      description: '',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Yes',
          value: 'yes',
        },
        {
          key: makeid(10),
          label: 'No',
          value: 'no',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'maNurseReportCompleted',
      type: 'checkbox',
      title: <p>MA → Nurse Report Completed</p>,
      description: '',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Yes',
          value: 'yes',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'callConclusionUnderstanding',
      type: 'radio',
      title: <p>Call Conclusion - Understanding</p>,
      description: (
        <ScriptWrapper>
          "Before we move forward, do you have any questions about what will
          happen next?"
          <br />
          "Thank you for bringing this to our attention — we want to make sure
          you're supported."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient verbalized full understanding',
          value: 'patientVerbalizedFullUnderstanding',
        },
        {
          key: makeid(10),
          label: 'Patient verbalized partial understanding',
          value: 'patientVerbalizedPartialUnderstanding',
        },
        {
          key: makeid(10),
          label: 'Patient unable to verbalize understanding',
          value: 'patientUnableToVerbalizeUnderstanding',
        },
      ],
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TInternalNurseEscalationTemplateValues = ExtractStepValues<
  ReturnType<typeof createInternalNurseEscalationTemplate>
>;

export const createInternalNurseEscalationBlurb = (
  data: TInternalNurseEscalationTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    patientConcernIdentified,
    escalationDetermination,
    patientNotifiedOfEscalation,
    resistanceEncountered,
    warmHandoffAttempted,
    nurseCallbackRequired,
    maNurseReportCompleted,
    callConclusionUnderstanding,
  } = data;
  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');
    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    patientConcernIdentified?.length &&
    !hasField(ctx, 'patientConcernIdentified')
  ) {
    addField(ctx, 'patientConcernIdentified');
    const map = {
      medicalAdviceRelated: 'Medical advice related',
      symptomRelated: 'Symptom-related',
      medicationRelated: 'Medication-related',
      otherOutOfScopeConcern: 'Other out-of-scope concern',
    };
    const items = mapValuesToText(patientConcernIdentified, map);

    if (items.length) {
      txt.push(
        `Patient contacted Tellihealth regarding ${formatList(
          items,
        )}, which is outside medical assistant scope.`,
      );
    }
  }

  if (
    escalationDetermination.length &&
    !hasField(ctx, 'escalationDetermination')
  ) {
    addField(ctx, 'escalationDetermination');
    txt.push(
      `Concern identified as requiring nursing review and escalation per protocol.`,
    );
  }

  if (
    patientNotifiedOfEscalation.length &&
    !hasField(ctx, 'patientNotifiedOfEscalation')
  ) {
    addField(ctx, 'patientNotifiedOfEscalation');
    txt.push(
      `Patient informed that concern requires nurse review and escalation process explained.`,
    );
  }

  if (resistanceEncountered && !hasField(ctx, 'resistanceEncountered')) {
    addField(ctx, 'resistanceEncountered');
    const map = {
      noResistance: 'no resistance',
      mildResistance: 'mild resistance',
      significantResistance: 'significant resistance',
    };
    txt.push(
      `Patient response to escalation was ${map[resistanceEncountered]}. Escalation proceeded per protocol.`,
    );
  }

  if (warmHandoffAttempted && !hasField(ctx, 'warmHandoffAttempted')) {
    addField(ctx, 'warmHandoffAttempted');
    const map = {
      completed: 'completed',
      attemptedButNurseUnavailable: 'attempted but unavailable',
      notAttemptedNurseUnavailable: 'not available',
    };

    txt.push(
      `Warm handoff to nurse ${map[warmHandoffAttempted]}. Callback plan discussed as appropriate.`,
    );
  }

  if (nurseCallbackRequired && !hasField(ctx, 'nurseCallbackRequired')) {
    addField(ctx, 'nurseCallbackRequired');
    const map = {
      yes: 'required',
      no: 'not required',
    };
    txt.push(
      `Nurse callback ${map[nurseCallbackRequired]} based on availability and concern type.`,
    );
  }

  if (
    maNurseReportCompleted.length &&
    !hasField(ctx, 'maNurseReportCompleted')
  ) {
    addField(ctx, 'maNurseReportCompleted');
    txt.push(
      `MA provided nurse with patient details, reason for escalation, and relevant context.`,
    );
  }

  if (
    callConclusionUnderstanding &&
    !hasField(ctx, 'callConclusionUnderstanding')
  ) {
    addField(ctx, 'callConclusionUnderstanding');
    const map = {
      patientVerbalizedFullUnderstanding: 'verbalized full understanding',
      patientVerbalizedPartialUnderstanding: 'partial understanding',
      patientUnableToVerbalizeUnderstanding:
        'was unable to verbalize understanding',
    };
    txt.push(
      `Call concluded after confirming patient understanding of escalation process. Patient ${map[callConclusionUnderstanding]}.`,
    );
  }

  return txt.join(' ');
};
